/**
 * The area floating canvas UI must stay inside.
 *
 * Anything that floats over the drawing — a panel, a picker — has to be reachable:
 * pushed past the edge of the canvas it can no longer be grabbed, and on a tablet
 * there is no keyboard to dismiss it with.
 */

export interface CanvasBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The drawing canvas when there is one, the viewport otherwise. */
export function getCanvasBounds(): CanvasBounds {
  const canvas = document.getElementById("dr-canvas");
  if (canvas) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    }
  }
  return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
}

/** Clamp a value into [min, max], tolerating an inverted range. */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, Math.max(min, max)));
}

/**
 * Top-left corner that keeps a box of this size fully inside the canvas.
 * With no measurable area to clamp against, the position is left alone — pinning
 * everything to the corner would be worse than not clamping at all.
 */
export function clampBoxToCanvas(
  left: number,
  top: number,
  width: number,
  height: number,
  bounds = getCanvasBounds()
): { left: number; top: number } {
  if (bounds.width <= 0 || bounds.height <= 0) return { left, top };

  return {
    left: clamp(left, bounds.left, bounds.left + bounds.width - width),
    top: clamp(top, bounds.top, bounds.top + bounds.height - height),
  };
}
